/**
 * GOD SUMMONING MODAL ⚡
 * ═══════════════════════════════════════════════════════════════
 * 
 * Beautiful modal for summoning Council Gods to join the War Council.
 * Shows dormant gods, their abilities, and summoning costs.
 * 
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { X, Sparkles, Zap, Lock, Check, Users } from 'lucide-react';
import { COUNCIL_GODS, WORKER_GODS } from './CouncilGods';

const GodCard = ({ god, isSelected, onToggle, isDormant, isWorker }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <button
      onClick={() => !isDormant && !isWorker && onToggle(god.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={isDormant}
      className={`
        relative p-4 rounded-xl border transition-all duration-300 text-left w-full
        ${isDormant 
          ? 'bg-gray-900/50 border-gray-800 opacity-50 cursor-not-allowed' 
          : isSelected
            ? `bg-gradient-to-br ${god.bgColor} border-2 ${god.borderColor} shadow-lg ${god.glowColor}`
            : isWorker
              ? `bg-gradient-to-br ${god.bgColor} border ${god.borderColor} opacity-70`
              : `bg-gray-800/50 border-gray-700 hover:border-gray-600 hover:bg-gray-800`
        }
        ${!isDormant && !isWorker && 'cursor-pointer hover:scale-[1.02]'}
      `}
      data-testid={`god-card-${god.id}`}
    >
      {/* Dormant Overlay */}
      {isDormant && (
        <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="text-center">
            <Lock className="w-6 h-6 text-gray-500 mx-auto mb-1" />
            <span className="text-xs text-gray-500">Locked</span>
          </div>
        </div>
      )}
      
      {/* Selection Indicator */}
      {isSelected && !isDormant && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
      
      {/* Worker Badge */}
      {isWorker && (
        <div className="absolute top-2 right-2 px-2 py-0.5 bg-gray-700 rounded-full">
          <span className="text-xs text-gray-400">Active</span>
        </div>
      )}
      
      {/* God Info */}
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center text-2xl
          ${isDormant ? 'bg-gray-800 grayscale' : `bg-gradient-to-br ${god.color}`}
          ${isHovered && !isDormant ? 'animate-pulse' : ''}
          shadow-lg
        `}>
          {god.emoji}
        </div>
        
        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`font-bold ${isDormant ? 'text-gray-500' : god.textColor}`}>
              {god.name}
            </h3>
            {god.summonCost && !isWorker && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                isDormant ? 'bg-gray-800 text-gray-500' : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {god.summonCost} ⚡
              </span>
            )}
          </div>
          <p className={`text-xs ${isDormant ? 'text-gray-600' : 'text-gray-400'}`}>
            {god.domain}
          </p>
          <p className={`text-xs mt-1 ${isDormant ? 'text-gray-600' : 'text-gray-500'} line-clamp-2`}>
            {god.description}
          </p>
        </div>
      </div>
      
      {/* Abilities (on hover or selected) */}
      {(isHovered || isSelected) && !isDormant && (
        <div className="mt-3 pt-3 border-t border-gray-700/50">
          <div className="flex flex-wrap gap-1">
            {god.abilities?.slice(0, 3).map((ability, i) => (
              <span key={i} className="text-xs px-2 py-0.5 bg-gray-700/50 rounded-full text-gray-400">
                {ability}
              </span>
            ))}
          </div>
        </div>
      )}
    </button>
  );
};

const SummoningModal = ({ 
  isOpen, 
  onClose, 
  onSummon, 
  activeWorkerGods = ['athena', 'hermes', 'apollo'],
  userCredits = 50,
  isPro = false
}) => {
  const [selectedGods, setSelectedGods] = useState([]);
  
  if (!isOpen) return null;
  
  const toggleGod = (godId) => {
    setSelectedGods(prev => 
      prev.includes(godId) 
        ? prev.filter(id => id !== godId)
        : [...prev, godId]
    );
  };
  
  const totalCost = selectedGods.reduce((sum, godId) => {
    const god = COUNCIL_GODS[godId];
    return sum + (god?.summonCost || 0);
  }, 0);
  
  const canSummon = selectedGods.length > 0 && (isPro || totalCost <= userCredits);
  
  const handleSummon = () => {
    onSummon(selectedGods);
    onClose();
  };
  
  const workerGods = Object.values(WORKER_GODS).filter(g => activeWorkerGods.includes(g.id));
  const councilGods = Object.values(COUNCIL_GODS);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="summoning-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-gray-900 rounded-2xl border border-purple-500/30 shadow-2xl overflow-hidden flex flex-col">
        {/* Animated top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 animate-pulse" />
        
        {/* Header */}
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                Summon Your Council
                <Sparkles className="w-5 h-5 text-yellow-400" />
              </h2>
              <p className="text-sm text-gray-400">
                Select gods to join the War Council debate
              </p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Active Worker Gods */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-4 h-4 text-green-400" />
              <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wide">
                Active Worker Gods
              </h3>
              <span className="text-xs text-gray-500">(Already in your arsenal)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {workerGods.map(god => (
                <GodCard
                  key={god.id}
                  god={god}
                  isSelected={false}
                  onToggle={() => {}}
                  isDormant={false}
                  isWorker={true}
                />
              ))}
            </div>
          </div>
          
          {/* Council Gods */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wide">
                The Shadow Council
              </h3>
              <span className="text-xs text-gray-500">(Summon to join debate)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {councilGods.map(god => (
                <GodCard
                  key={god.id}
                  god={god}
                  isSelected={selectedGods.includes(god.id)}
                  onToggle={toggleGod}
                  isDormant={!isPro && god.summonCost > userCredits}
                  isWorker={false}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-gray-800 bg-gray-900/50">
          <div className="flex items-center justify-between">
            {/* Cost Summary */}
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <span className="text-gray-400">Selected: </span>
                <span className="text-white font-bold">{selectedGods.length} gods</span>
              </div>
              {!isPro && (
                <div className="text-sm">
                  <span className="text-gray-400">Cost: </span>
                  <span className={`font-bold ${totalCost > userCredits ? 'text-red-400' : 'text-yellow-400'}`}>
                    {totalCost} ⚡
                  </span>
                  <span className="text-gray-500 ml-1">/ {userCredits} available</span>
                </div>
              )}
              {isPro && (
                <span className="px-2 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs text-purple-400">
                  Pro: Unlimited Summons
                </span>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSummon}
                disabled={!canSummon}
                className={`
                  px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 transition-all
                  ${canSummon
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white shadow-lg shadow-purple-500/25'
                    : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  }
                `}
                data-testid="summon-council-btn"
              >
                <Sparkles className="w-4 h-4" />
                Summon Council
              </button>
            </div>
          </div>
          
          {/* Hint */}
          {selectedGods.length === 0 && (
            <p className="text-xs text-gray-500 mt-3 text-center">
              Select at least one Council god to join the debate
            </p>
          )}
          {selectedGods.length > 0 && selectedGods.length < 3 && (
            <p className="text-xs text-gray-500 mt-3 text-center">
              Tip: 3-5 gods create the most dynamic debates
            </p>
          )}
        </div>
      </div>
      
      {/* Animation styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default SummoningModal;
